import { BookingServiceClient } from 'fitbeat.api-clients';
import { ProcessTypes, UserProfile, UserWorkoutSession } from 'fitbeat.gym-core';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import FontAwesomeIcon from 'react-native-vector-icons/Feather';
import { connect } from 'react-redux';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import { getApiConfig } from '../../config';
import Theme from '../../theme';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { defaultScale } from '../../utils/scaling';
import { bookingInviteContinue, getNextBookingStep, shareInvite, skipInvite } from '../actions';
import {
  BookingFlowFooter,
  BookingInstructionHeader,
  BookingInstructionSubtitle,
} from '../components';
import { shareOpenInviteLink } from '../utils';
import { styles as sharedStyles } from './Styles';
import { isWeb } from '../../utils/ScreenSizes';

interface IProps {
  userProfile: UserProfile;
  workoutSession: UserWorkoutSession;
  t(key: string, options?: any): string;
  getNextBookingStep(userProfile: UserProfile, processType: ProcessTypes): void;
  shareInvite(): void;
  skipInvite(): void;
  bookingInviteContinue(shareLink: string | null, workoutSessionId: string): void;
}

interface IState {
  link: string | null;
  isLoading: boolean;
  error: string | null;
  hasSharedLink: boolean;
  copyLinkMessage: string | null;
}

class InviteHome extends Component<IProps, IState> {
  public state = {
    link: null,
    isLoading: true,
    error: null,
    hasSharedLink: false,
    copyLinkMessage: null,
  };
  private bookingServiceClient = new BookingServiceClient(getApiConfig(), fetch, new AuthTokenBuilder());

  public componentDidMount = async () => {
    try {
      await this.setState({ isLoading: true });
      const { workoutSession } = this.props;
      const { link } = await this.bookingServiceClient.getOpenInviteLinkForWorkout(workoutSession.uniqueReference);
      await this.setState({ link, isLoading: false });
    } catch (error) {
      await this.setState({ error: error.message, isLoading: false });
    }
  }

  public continue = () => {
    const { userProfile, getNextBookingStep, skipInvite, bookingInviteContinue, workoutSession } = this.props;
    const { hasSharedLink } = this.state;
    if (!hasSharedLink) {
      skipInvite();
    }
    bookingInviteContinue(this.state.link, workoutSession.uniqueReference);
    getNextBookingStep(userProfile, ProcessTypes.BookingMade);
  }

  public share = async () => {
    const { t, userProfile, shareInvite, workoutSession } = this.props;
    const { link } = this.state;
    this.setState({ hasSharedLink: true });
    shareInvite();
    !isWeb() && await shareOpenInviteLink(t, link, userProfile, new Date(workoutSession.startTimeOfFirstExercise))
  }

  public copyLinkToClipboard = () => {
    const { link } = this.state;

    link &&
      navigator.clipboard.writeText(link)
        .then(() => this.setState({ copyLinkMessage: 'Copied!' }))
        .catch((err) => {
          this.setState({ copyLinkMessage: 'Unable to copy' })
        });

  };

  public render() {
    const { t, userProfile } = this.props;
    const { isLoading, error, hasSharedLink, copyLinkMessage } = this.state;
    return (
      <View style={sharedStyles.container}>
        <View style={styles.container}>
          {error ?
            <Text style={styles.error}>Oops something went wrong</Text>
            : isLoading ?
              <ActivityIndicator color={Theme.PRIMARY} />
              :
              <>
                <BookingInstructionHeader header={t('inviteAFriend')} />
                {!isWeb() ? (
                  <Button
                    title={t('shareInvite')}
                    fontColor={Theme.WHITE}
                    size={ButtonSize.LARGE}
                    buttonType={ButtonTypes.NORMAL}
                    onPress={this.share}
                    fontSize={defaultScale(20)}
                    containerStyle={{ marginBottom: defaultScale(5), width: '100%' }}
                    icon={<FontAwesomeIcon name='share' size={defaultScale(20)} color={Theme.WHITE} style={{ paddingRight: 10 }} />}
                  />
                ) : (
                  <>
                    <Text style={styles.copyLinkText}>{t('copyInviteInstruction')}</Text>
                    <Text style={styles.inviteLink} onPress={this.copyLinkToClipboard}>{this.state.link}</Text>
                    {copyLinkMessage && <Text style={styles.linkMessage}>{copyLinkMessage}</Text>}
                  </>
                )}
                <BookingInstructionSubtitle header={t('inviteToJoinWorkout')} />
              </>
          }
        </View>
        <BookingFlowFooter style={styles.footer}>
          {!isLoading &&
            <>
              <Button
                title={hasSharedLink ? t('next') : t('skip')}
                fontColor={Theme.PRIMARY}
                size={ButtonSize.MEDIUM}
                buttonType={ButtonTypes.OUTLINE}
                onPress={this.continue}
              />
            </>
          }
        </BookingFlowFooter>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...Theme.textStyle.heading,
    paddingTop: defaultScale(10),
    textAlign: 'center',
    color: Theme.CHARCOAL,
  },
  footer: {
    paddingVertical: defaultScale(10),
  },
  error: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
    color: Theme.CHARCOAL,
  },
  linkContainer: {
    display: "flex",
    flexDirection: 'row',
  },
  copyLinkText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  inviteLink: {
    ...Theme.textStyle.bodyCopyMed,
    color: Theme.PRIMARY,
  },
  linkMessage: {
    ...Theme.textStyle.bodyCopyMed,
  },
});

const mapStateToProps = (state) => {
  return {
    workoutSession: state.bookings.bookingWizard.workoutSession,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getNextBookingStep: (userProfile, processType) => dispatch(getNextBookingStep(userProfile, processType)),
    shareInvite: () => dispatch(shareInvite()),
    skipInvite: () => dispatch(skipInvite()),
    bookingInviteContinue: (shareLink, workoutSessionId) =>
      dispatch(bookingInviteContinue(shareLink, workoutSessionId)),
  };
};

export const InviteHomeStep = translate(['bookings'], { wait: true })(
  connect(mapStateToProps, mapDispatchToProps)(InviteHome));
